import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import { affiliates } from '@/lib/marketing-data';
import {
  ArrowRight,
  DollarSign,
  Clock,
  CreditCard,
  BarChart3,
  Users,
  Gift,
  Zap,
} from 'lucide-react';

export const Route = createFileRoute('/affiliates')({
  component: AffiliatesPage,
});

function AffiliatesPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="pt-32 pb-16 sm:pt-40 sm:pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div className="inline-flex items-center gap-2 rounded-full border bg-green-500/10 px-4 py-1.5 text-green-600 mb-6">
                <Gift className="h-4 w-4" />
                <span className="text-sm font-medium">Affiliate Program</span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
                {affiliates.headline}
              </h1>
              <p className="mt-6 text-xl text-muted-foreground">
                {affiliates.subheadline}
              </p>
              <div className="mt-10">
                <Link to={affiliates.cta.href}>
                  <Button size="lg" className="gap-2">
                    {affiliates.cta.text} <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {affiliates.benefits.map((benefit, index) => {
                const icons = [DollarSign, Clock, CreditCard, BarChart3];
                const Icon = icons[index] || DollarSign;
                return (
                  <div
                    key={benefit.title}
                    className="rounded-xl border bg-card p-6 text-center"
                  >
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10 mx-auto">
                      <Icon className="h-7 w-7 text-green-600" />
                    </div>
                    <h2 className="mt-4 text-lg font-semibold">{benefit.title}</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {benefit.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                How It Works
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Start earning in three simple steps.
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-3">
              {[
                {
                  step: '01',
                  title: 'Sign Up',
                  description:
                    'Create your affiliate account in seconds. No approval process required.',
                  icon: Users,
                },
                {
                  step: '02',
                  title: 'Share',
                  description:
                    'Share your unique referral link with your audience. We provide marketing materials.',
                  icon: Zap,
                },
                {
                  step: '03',
                  title: 'Earn',
                  description:
                    'Get 30% of every payment made by customers you refer, forever.',
                  icon: DollarSign,
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-2xl font-bold mx-auto">
                    {item.step}
                  </div>
                  <h3 className="mt-6 text-xl font-semibold">{item.title}</h3>
                  <p className="mt-2 text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Earnings Calculator */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  Earnings Potential
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  See how much you could earn as a ForgeNetwork affiliate.
                </p>
              </div>

              <div className="rounded-xl border bg-card p-8">
                <div className="grid gap-6 sm:grid-cols-3">
                  {[
                    {
                      referrals: '10',
                      monthly: '$87',
                      yearly: '$1,044',
                    },
                    {
                      referrals: '50',
                      monthly: '$435',
                      yearly: '$5,220',
                    },
                    {
                      referrals: '100',
                      monthly: '$870',
                      yearly: '$10,440',
                    },
                  ].map((tier) => (
                    <div
                      key={tier.referrals}
                      className="rounded-lg border bg-muted/50 p-6 text-center"
                    >
                      <div className="text-3xl font-bold text-primary">
                        {tier.referrals}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Pro Referrals
                      </div>
                      <div className="mt-4 text-xl font-semibold">
                        {tier.monthly}/mo
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {tier.yearly}/year
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-center text-sm text-muted-foreground">
                  Based on Pro plan ($29/mo) with 30% commission
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-primary p-8 sm:p-16 text-center">
              <h2 className="text-3xl font-bold text-primary-foreground sm:text-4xl">
                Start Earning Today
              </h2>
              <p className="mt-4 text-lg text-primary-foreground/80 max-w-2xl mx-auto">
                Join our affiliate program and start earning recurring commissions.
              </p>
              <div className="mt-8">
                <Link to={affiliates.cta.href}>
                  <Button
                    size="lg"
                    variant="secondary"
                    className="gap-2 bg-white text-primary hover:bg-white/90"
                  >
                    {affiliates.cta.text} <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
